import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { PersonnelManagementPanel } from './PersonnelPage'

// ── Types ──────────────────────────────────────────────────────────────────

interface Member {
  id: string
  display_name: string
  username: string | null
  email: string
  role: 'member' | 'manager' | 'admin'
  region_code: string
  group_name: string
  is_group_lead: boolean
  is_active: boolean
  account_status: string
  visible_group_scope: string[] | null
}

interface Invitation {
  id: string
  email: string
  display_name: string
  region_code: string
  group_name: string
  role: string
  expires_at: string
}

type Tab = 'roles' | 'scope' | 'sysconfig' | 'personnel'

type Modal =
  | { type: 'inviteManager' }
  | { type: 'inviteMember' }
  | { type: 'promote'; member: Member }
  | { type: 'demote'; member: Member }
  | { type: 'deactivate'; member: Member }

// ── Styles ─────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '13px',
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

const selectStyle: React.CSSProperties = { ...inputStyle, background: 'var(--bg-surface)' }

const btnBase: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
  background: 'var(--bg-surface)',
}

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: 'var(--accent-contrast)',
}

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: 'var(--danger)',
  borderColor: 'var(--danger)',
  color: 'var(--accent-contrast)',
}

const btnWarn: React.CSSProperties = {
  ...btnBase,
  background: 'var(--warning)',
  borderColor: 'var(--warning)',
  color: 'var(--accent-contrast)',
}

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  fontSize: '13px',
  verticalAlign: 'middle',
}

const thStyle: React.CSSProperties = {
  ...tdStyle,
  background: 'var(--bg-page)',
  fontWeight: 600,
  fontSize: '12px',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
}

const GROUPS = ['CN-A', 'CN-B', 'US']
const REGIONS = ['CN', 'US']

// ── Helpers ─────────────────────────────────────────────────────────────────

function apiFetch(path: string, token: string, opts: RequestInit = {}) {
  return fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers ?? {}),
    },
  })
}

// ── Modal Overlay ───────────────────────────────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: '8px', padding: '24px',
        minWidth: '360px', maxWidth: '520px', width: '100%',
      }}>
        {children}
      </div>
    </div>
  )
}

// ── ThemedModalOverlay (uses §3.5 tokens — for new T003+ components) ────────

function ThemedModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: '8px', padding: '24px',
        minWidth: '360px', maxWidth: '520px', width: '100%',
        border: '1px solid var(--border)',
      }}>
        {children}
      </div>
    </div>
  )
}

// ── InviteManagerModal ──────────────────────────────────────────────────────

function InviteManagerModal({ token, onClose, onDone }: {
  token: string; onClose: () => void; onDone: () => void
}) {
  const { t } = useLang()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [region, setRegion] = useState('CN')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await apiFetch('/api/invitations', token, {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(), display_name: displayName.trim(),
          region_code: region, role: 'manager',
        }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      onDone()
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('adminInviteManager')}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('adminMemberEmail')}
          <input style={{ ...inputStyle, marginTop: '4px' }} value={email}
            onChange={e => setEmail(e.target.value)} type="email" />
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('adminMemberName')}
          <input style={{ ...inputStyle, marginTop: '4px' }} value={displayName}
            onChange={e => setDisplayName(e.target.value)} />
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('region')}
          <select style={{ ...selectStyle, marginTop: '4px' }} value={region}
            onChange={e => setRegion(e.target.value)}>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        {error && <p style={{ color: 'var(--danger)', fontSize: '13px', margin: 0 }}>{error}</p>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button style={btnBase} onClick={onClose} disabled={loading}>{t('personnelCancel')}</button>
          <button
            style={btnPrimary}
            onClick={submit}
            disabled={loading || !email.trim() || !displayName.trim()}
          >
            {loading ? t('loading') : t('personnelSubmit')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── InviteMemberModal (T003 — uses §3.5 theme tokens from birth) ───────────

function InviteMemberModal({ token, onClose, onDone }: {
  token: string; onClose: () => void; onDone: () => void
}) {
  const { t } = useLang()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [region, setRegion] = useState('CN')
  const [group, setGroup] = useState('CN-A')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const themedInput: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--bg-page)',
    color: 'var(--text-primary)',
  }

  const themedSelect: React.CSSProperties = { ...themedInput }

  const submit = async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await apiFetch('/api/invitations', token, {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(), display_name: displayName.trim(),
          region_code: region, group_name: group, role: 'member',
        }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      onDone()
    } finally {
      setLoading(false)
    }
  }

  return (
    <ThemedModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: 'var(--text-primary)' }}>
        + {t('adminInviteMember')}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('adminMemberEmail')}
          <input style={{ ...themedInput, marginTop: '4px' }} value={email}
            onChange={e => setEmail(e.target.value)} type="email" />
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('adminMemberName')}
          <input style={{ ...themedInput, marginTop: '4px' }} value={displayName}
            onChange={e => setDisplayName(e.target.value)} />
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          <span style={{ display: 'block', marginBottom: '4px' }}>{t('adminMemberRole')}</span>
          <span style={{
            display: 'inline-block', padding: '4px 8px', borderRadius: '4px',
            fontSize: '12px', background: 'var(--bg-elevated)',
            color: 'var(--text-muted)', border: '1px solid var(--border)',
          }}>
            member（固定）
          </span>
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('region')}
          <select style={{ ...themedSelect, marginTop: '4px' }} value={region}
            onChange={e => setRegion(e.target.value)}>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t('group')}
          <select style={{ ...themedSelect, marginTop: '4px' }} value={group}
            onChange={e => setGroup(e.target.value)}>
            {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        {error && <p style={{ color: 'var(--danger)', fontSize: '13px', margin: 0 }}>{error}</p>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button
            style={{
              padding: '4px 10px', border: '1px solid var(--border)', borderRadius: '4px',
              fontSize: '12px', cursor: 'pointer', background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
            }}
            onClick={onClose}
            disabled={loading}
          >
            {t('personnelCancel')}
          </button>
          <button
            style={{
              padding: '4px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
              background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none',
              opacity: (loading || !email.trim() || !displayName.trim()) ? 0.6 : 1,
            }}
            onClick={submit}
            disabled={loading || !email.trim() || !displayName.trim()}
          >
            {loading ? t('loading') : t('personnelSubmit')}
          </button>
        </div>
      </div>
    </ThemedModalOverlay>
  )
}

// ── PromoteConfirmModal ─────────────────────────────────────────────────────

function PromoteConfirmModal({ member, token, onClose, onDone }: {
  member: Member; token: string; onClose: () => void; onDone: () => void
}) {
  const { t } = useLang()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await apiFetch(`/api/admin/members/${member.id}/promote`, token, {
        method: 'POST', body: '{}',
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      onDone()
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('adminPromote')}</h3>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        {t('adminPromoteConfirm').replace('{name}', member.display_name)}
      </p>
      {error && <p style={{ color: 'var(--danger)', fontSize: '13px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button style={btnBase} onClick={onClose} disabled={loading}>{t('personnelCancel')}</button>
        <button style={btnPrimary} onClick={submit} disabled={loading}>
          {loading ? t('loading') : t('personnelConfirm')}
        </button>
      </div>
    </ModalOverlay>
  )
}

// ── DemoteModal ─────────────────────────────────────────────────────────────

function DemoteModal({ member, token, onClose, onDone }: {
  member: Member; token: string; onClose: () => void; onDone: () => void
}) {
  const { t } = useLang()
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await apiFetch(`/api/admin/members/${member.id}/demote`, token, {
        method: 'POST',
        body: JSON.stringify({ display_name_confirm: confirm }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      onDone()
    } finally {
      setLoading(false)
    }
  }

  const bodyText = t('adminDemoteBody').replace('{name}', member.display_name)

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('adminDemoteTitle')}</h3>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>{bodyText}</p>
      <input
        style={inputStyle}
        placeholder={t('adminDemoteInput')}
        value={confirm}
        onChange={e => setConfirm(e.target.value)}
      />
      {error && <p style={{ color: 'var(--danger)', fontSize: '13px', marginTop: '8px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button style={btnBase} onClick={onClose} disabled={loading}>{t('personnelCancel')}</button>
        <button
          style={btnDanger}
          onClick={submit}
          disabled={loading || confirm.trim() !== member.display_name}
        >
          {loading ? t('loading') : t('adminDemoteConfirm')}
        </button>
      </div>
    </ModalOverlay>
  )
}

// ── DeactivateConfirmModal ──────────────────────────────────────────────────

function DeactivateConfirmModal({ member, token, onClose, onDone }: {
  member: Member; token: string; onClose: () => void; onDone: () => void
}) {
  const { t } = useLang()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await apiFetch(`/api/admin/members/${member.id}/deactivate`, token, {
        method: 'POST', body: '{}',
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      onDone()
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('adminDeactivate')}</h3>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        {t('adminDeactivateConfirm').replace('{name}', member.display_name)}
      </p>
      {error && <p style={{ color: 'var(--danger)', fontSize: '13px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button style={btnBase} onClick={onClose} disabled={loading}>{t('personnelCancel')}</button>
        <button style={btnWarn} onClick={submit} disabled={loading}>
          {loading ? t('loading') : t('personnelConfirm')}
        </button>
      </div>
    </ModalOverlay>
  )
}

// ── RoleManagementTab ───────────────────────────────────────────────────────

function RoleManagementTab({ token, onRefresh }: { token: string; onRefresh: () => void }) {
  const { t } = useLang()
  const [members, setMembers] = useState<Member[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState<Modal | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [mResp, iResp] = await Promise.all([
        apiFetch('/api/members', token),
        apiFetch('/api/invitations', token),
      ])
      if (mResp.ok) {
        const data = await mResp.json()
        setMembers(data.members ?? [])
      }
      if (iResp.ok) {
        const data = await iResp.json()
        setInvitations(data.invitations ?? [])
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchData() }, [fetchData])

  const handleModalDone = () => {
    setModal(null)
    fetchData()
    onRefresh()
  }

  const activeMembers = members.filter(m => m.account_status !== 'deactivated')
  const adminMembers = activeMembers.filter(m => m.role === 'admin')
  const managerMembers = activeMembers.filter(m => m.role === 'manager')
  const memberMembers = activeMembers.filter(m => m.role === 'member')
  const pendingManagerInvitations = invitations.filter((inv: Invitation) => inv.role === 'manager')
  const pendingMemberInvitations = invitations.filter((inv: Invitation) => inv.role === 'member')

  const renderRoleChip = (role: string) => {
    const colors: Record<string, string> = {
      admin: '#7c3aed', manager: '#1a73e8', member: '#555',
    }
    return (
      <span style={{
        display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
        fontSize: '11px', background: colors[role] ?? '#eee', color: '#fff',
      }}>
        {role}
      </span>
    )
  }

  const renderStatusChip = (m: Member) => {
    if (!m.is_active) return <span style={{ color: 'var(--warning)', fontSize: '12px' }}>停用</span>
    if (m.account_status === 'locked') return <span style={{ color: 'var(--danger)', fontSize: '12px' }}>锁定</span>
    return <span style={{ color: 'var(--success)', fontSize: '12px' }}>正常</span>
  }

  const renderRow = (m: Member) => (
    <tr key={m.id}>
      <td style={tdStyle}>{m.display_name}{m.is_group_lead ? ' 🎖' : ''}</td>
      <td style={tdStyle}>{m.email}</td>
      <td style={tdStyle}>{renderRoleChip(m.role)}</td>
      <td style={tdStyle}>{m.group_name}</td>
      <td style={tdStyle}>{renderStatusChip(m)}</td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {m.role === 'member' && m.is_active && (
            <button style={btnPrimary} onClick={() => setModal({ type: 'promote', member: m })}>
              {t('adminPromote')}
            </button>
          )}
          {m.role === 'manager' && m.is_active && (
            <button style={btnWarn} onClick={() => setModal({ type: 'demote', member: m })}>
              {t('adminDemote')}
            </button>
          )}
          {m.is_active && m.role !== 'admin' && (
            <button style={btnDanger} onClick={() => setModal({ type: 'deactivate', member: m })}>
              {t('adminDeactivate')}
            </button>
          )}
          {m.is_active && m.role === 'admin' && adminMembers.length > 1 && (
            <button style={btnDanger} onClick={() => setModal({ type: 'deactivate', member: m })}>
              {t('adminDeactivate')}
            </button>
          )}
        </div>
      </td>
    </tr>
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>{t('adminTabRoles')}</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{
              padding: '4px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
              background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none',
            }}
            onClick={() => setModal({ type: 'inviteMember' })}
          >
            + {t('adminInviteMember')}
          </button>
          <button style={btnPrimary} onClick={() => setModal({ type: 'inviteManager' })}>
            + {t('adminInviteManager')}
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('loading')}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={thStyle}>{t('adminMemberName')}</th>
              <th style={thStyle}>{t('adminMemberEmail')}</th>
              <th style={thStyle}>{t('adminMemberRole')}</th>
              <th style={thStyle}>{t('adminMemberGroup')}</th>
              <th style={thStyle}>{t('adminMemberStatus')}</th>
              <th style={thStyle}>{t('adminActions')}</th>
            </tr>
          </thead>
          <tbody>
            {adminMembers.map(renderRow)}
            {managerMembers.map(renderRow)}
            {memberMembers.map(renderRow)}
          </tbody>
        </table>
      )}

      {pendingManagerInvitations.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>{t('adminPendingInvitations')}</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('adminMemberName')}</th>
                <th style={thStyle}>{t('adminMemberEmail')}</th>
                <th style={thStyle}>{t('adminMemberGroup')}</th>
                <th style={thStyle}>{t('personnelExpiresAt')}</th>
              </tr>
            </thead>
            <tbody>
              {pendingManagerInvitations.map(inv => (
                <tr key={inv.id}>
                  <td style={tdStyle}>{inv.display_name}</td>
                  <td style={tdStyle}>{inv.email}</td>
                  <td style={tdStyle}>{inv.group_name}</td>
                  <td style={tdStyle}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingMemberInvitations.length > 0 && (
        <div style={{
          marginTop: '24px', border: '1px solid var(--border)', borderRadius: '6px', padding: '16px',
          background: 'var(--bg-surface)',
        }}>
          <h4 style={{ fontSize: '14px', marginBottom: '8px', color: 'var(--text-primary)', margin: '0 0 12px' }}>
            {t('adminPendingMemberInvitations')}
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {t('adminMemberName')}
                </th>
                <th style={{ ...thStyle, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {t('adminMemberEmail')}
                </th>
                <th style={{ ...thStyle, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {t('adminMemberGroup')}
                </th>
                <th style={{ ...thStyle, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {t('personnelExpiresAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pendingMemberInvitations.map(inv => (
                <tr key={inv.id}>
                  <td style={{ ...tdStyle, color: 'var(--text-primary)', borderColor: 'var(--border)' }}>
                    {inv.display_name}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-primary)', borderColor: 'var(--border)' }}>
                    {inv.email}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-primary)', borderColor: 'var(--border)' }}>
                    {inv.group_name}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)', borderColor: 'var(--border)' }}>
                    {new Date(inv.expires_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'inviteManager' && (
        <InviteManagerModal token={token} onClose={() => setModal(null)} onDone={handleModalDone} />
      )}
      {modal?.type === 'inviteMember' && (
        <InviteMemberModal token={token} onClose={() => setModal(null)} onDone={handleModalDone} />
      )}
      {modal?.type === 'promote' && (
        <PromoteConfirmModal member={modal.member} token={token} onClose={() => setModal(null)} onDone={handleModalDone} />
      )}
      {modal?.type === 'demote' && (
        <DemoteModal member={modal.member} token={token} onClose={() => setModal(null)} onDone={handleModalDone} />
      )}
      {modal?.type === 'deactivate' && (
        <DeactivateConfirmModal member={modal.member} token={token} onClose={() => setModal(null)} onDone={handleModalDone} />
      )}
    </div>
  )
}

// ── ScopeConfigTab ──────────────────────────────────────────────────────────

function ScopeConfigTab({ token }: { token: string }) {
  const { t } = useLang()
  const [managers, setManagers] = useState<Member[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [scopeState, setScopeState] = useState<Record<string, string[]>>({})
  const [error, setError] = useState<string | null>(null)

  const fetchManagers = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await apiFetch('/api/members?role=manager', token)
      if (resp.ok) {
        const data = await resp.json()
        const mgrs: Member[] = (data.members ?? []).filter(
          (m: Member) => m.account_status !== 'deactivated' && m.is_active,
        )
        setManagers(mgrs)
        const initial: Record<string, string[]> = {}
        mgrs.forEach(m => { initial[m.id] = m.visible_group_scope ?? [] })
        setScopeState(initial)
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchManagers() }, [fetchManagers])

  const toggleGroup = (managerId: string, group: string) => {
    setScopeState(prev => {
      const current = prev[managerId] ?? []
      const next = current.includes(group)
        ? current.filter(g => g !== group)
        : [...current, group]
      return { ...prev, [managerId]: next }
    })
  }

  const saveScope = async (managerId: string) => {
    setSaving(managerId)
    setError(null)
    try {
      const resp = await apiFetch(`/api/admin/members/${managerId}/scope`, token, {
        method: 'PUT',
        body: JSON.stringify({ visible_group_scope: scopeState[managerId] ?? [] }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      setSaved(managerId)
      setTimeout(() => setSaved(null), 2000)
    } finally {
      setSaving(null)
    }
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('loading')}</p>
  if (managers.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('adminNoManagers')}</p>

  return (
    <div>
      <h3 style={{ margin: '0 0 16px', fontSize: '15px' }}>{t('adminScopeTitle')}</h3>
      {error && <p style={{ color: 'var(--danger)', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
      {managers.map(m => {
        const currentScope = scopeState[m.id] ?? []
        const isAllGroups = currentScope.length === 0

        return (
          <div key={m.id} style={{
            border: '1px solid var(--border)', borderRadius: '6px',
            padding: '16px', marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div>
                <strong style={{ fontSize: '14px' }}>{m.display_name}</strong>
                <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  {m.group_name} · {m.email}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {saved === m.id && (
                  <span style={{ fontSize: '12px', color: 'var(--success)' }}>✓ 已保存</span>
                )}
                <button
                  style={btnPrimary}
                  onClick={() => saveScope(m.id)}
                  disabled={saving === m.id}
                >
                  {saving === m.id ? t('loading') : t('adminScopeSave')}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isAllGroups}
                  onChange={() => {
                    if (!isAllGroups) setScopeState(prev => ({ ...prev, [m.id]: [] }))
                  }}
                />
                {t('adminScopeAllGroups')}
              </label>
              {GROUPS.map(g => (
                <label key={g} style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={currentScope.includes(g)}
                    onChange={() => toggleGroup(m.id, g)}
                  />
                  {g}
                </label>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── SystemConfigTab ─────────────────────────────────────────────────────────

const CONFIG_FIELDS: Array<{ key: string; labelKey: string; type: 'number' | 'text' }> = [
  { key: 'access_ttl', labelKey: 'adminSysConfigAccessTtl', type: 'number' },
  { key: 'refresh_ttl', labelKey: 'adminSysConfigRefreshTtl', type: 'number' },
  { key: 'lockout_threshold', labelKey: 'adminSysConfigLockoutThreshold', type: 'number' },
  { key: 'lockout_minutes', labelKey: 'adminSysConfigLockoutMinutes', type: 'number' },
  { key: 'backup_time', labelKey: 'adminSysConfigBackupTime', type: 'text' },
  { key: 'retention_days', labelKey: 'adminSysConfigRetentionDays', type: 'number' },
]

function SystemConfigTab({ token }: { token: string }) {
  const { t } = useLang()
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await apiFetch('/api/admin/system-config', token)
      if (resp.ok) {
        const data = await resp.json()
        setValues(data)
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchConfig() }, [fetchConfig])

  const saveConfig = async () => {
    setSaving(true)
    setError(null)
    try {
      const updates: Record<string, string | number> = {}
      for (const field of CONFIG_FIELDS) {
        const raw = values[field.key]
        if (raw !== undefined && raw !== '') {
          updates[field.key] = field.type === 'number' ? Number(raw) : raw
        }
      }
      const resp = await apiFetch('/api/admin/system-config', token, {
        method: 'PUT',
        body: JSON.stringify({ updates }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      setSavedMsg(true)
      setTimeout(() => setSavedMsg(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{t('loading')}</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>{t('adminSysConfigTitle')}</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {savedMsg && <span style={{ fontSize: '12px', color: 'var(--success)' }}>✓ {t('adminSysConfigSaved')}</span>}
          <button style={btnPrimary} onClick={saveConfig} disabled={saving}>
            {saving ? t('loading') : t('adminSysConfigSave')}
          </button>
        </div>
      </div>
      {error && <p style={{ color: 'var(--danger)', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {CONFIG_FIELDS.map(field => (
          <label key={field.key} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {t(field.labelKey)}
            <input
              style={{ ...inputStyle, marginTop: '4px' }}
              type={field.type === 'number' ? 'number' : 'text'}
              value={values[field.key] ?? ''}
              onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────────────────────

export function AdminConsolePage() {
  const { t } = useLang()
  const { accessToken } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('roles')
  const [refreshKey, setRefreshKey] = useState(0)

  if (!accessToken) return null

  const tabStyle = (tab: Tab): React.CSSProperties => ({
    padding: '8px 16px',
    border: 'none',
    background: activeTab === tab ? 'var(--accent)' : 'var(--bg-page)',
    color: activeTab === tab ? 'var(--accent-contrast)' : 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: '13px',
    borderRadius: '4px 4px 0 0',
    fontWeight: activeTab === tab ? 600 : 400,
  })

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>{t('navAdminConsole')}</h2>
        <Link
          to="/audit-log"
          style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}
        >
          {t('adminAuditLink')} →
        </Link>
      </div>

      <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid var(--accent)' }}>
        <button style={tabStyle('roles')} onClick={() => setActiveTab('roles')}>
          {t('adminTabRoles')}
        </button>
        <button style={tabStyle('scope')} onClick={() => setActiveTab('scope')}>
          {t('adminTabScope')}
        </button>
        <button style={tabStyle('sysconfig')} onClick={() => setActiveTab('sysconfig')}>
          {t('adminTabSysConfig')}
        </button>
        <button style={tabStyle('personnel')} onClick={() => setActiveTab('personnel')}>
          {t('navPersonnel')}
        </button>
      </div>

      <div style={{
        border: '1px solid var(--border)', borderTop: 'none',
        borderRadius: '0 0 6px 6px', padding: '20px',
      }}>
        {activeTab === 'roles' && (
          <RoleManagementTab
            key={refreshKey}
            token={accessToken}
            onRefresh={() => setRefreshKey(k => k + 1)}
          />
        )}
        {activeTab === 'scope' && <ScopeConfigTab token={accessToken} />}
        {activeTab === 'sysconfig' && <SystemConfigTab token={accessToken} />}
        {activeTab === 'personnel' && <PersonnelManagementPanel />}
      </div>
    </div>
  )
}

// ── test exports ─────────────────────────────────────────────────────────────
export { InviteManagerModal as InviteManagerModal_TEST, InviteMemberModal as InviteMemberModal_TEST }
